import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import '../global.css';
import { AuthFetch, useAuth } from '../hooks/useAuth';
import { LOOKUP_EXTRA_AREAS, RequestHistoryDomain, USED_IN_OPTIONS } from '../lib/request-history';
import { LookupField, LookupItem, LookupResponse } from '../types';
import { LookupTranslationLine } from './LookupTranslationLine';
import { Nav } from './Nav';
import { UsedInAreaFilters } from './UsedInAreaFilters';

// combined areas load whole docs, so they get fewer codes and smaller pages (mirrors the API's limits)
const MAX_CODES_COMBINED = 10;
const MAX_CODES_SINGLE = 1000;
const PAGE_SIZES_COMBINED = [1, 5, 10];
const PAGE_SIZES_SINGLE = [20, 50, 100];
// everything the tab shows differently for products and categories
const LOOKUP_UI: Record<
	RequestHistoryDomain,
	{
		// titles are what's reviewed most, so they're the default area
		defaultArea: string;
		titleLabel: string;
		noun: string;
		codesPlaceholder: string;
		// a category code is on many branches, and a category's locales come from its branch, so it shows them
		// instead of filtering on locale
		branchFilters: boolean;
		localeFilter: boolean;
	}
> = {
	ProductDomain: {
		defaultArea: 'ProductName',
		titleLabel: 'Name',
		noun: 'product',
		codesPlaceholder: 'Colour codes, separated by commas, spaces or new lines',
		branchFilters: false,
		localeFilter: true,
	},
	CategoryMetaDomain: {
		defaultArea: 'CategoryMetaTitle',
		titleLabel: 'Title',
		noun: 'category',
		codesPlaceholder: 'Category codes, separated by commas, spaces or new lines',
		branchFilters: true,
		localeFilter: false,
	},
};

const isCombinedArea = (domain: RequestHistoryDomain | '', area: string) =>
	!!domain && LOOKUP_EXTRA_AREAS[domain].some(({ value }) => value === area);

// codes can be separated by commas, whitespace or new lines
const parseCodes = (value: string) => [...new Set(value.split(/[\s,]+/).map((code) => code.trim()).filter(Boolean))];

export const Lookup = () => {
	const [requestHistory, setRequestHistory] = useState<RequestHistoryDomain | ''>('');
	const [area, setArea] = useState('');
	const [branch, setBranch] = useState('');
	const [locale, setLocale] = useState('');
	const [excludeEnGbBranches, setExcludeEnGbBranches] = useState(true);
	const [codesText, setCodesText] = useState('');
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(PAGE_SIZES_SINGLE[0]);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const [isExporting, setIsExporting] = useState(false);
	const [exportError, setExportError] = useState<string | null>(null);
	// what Search was last clicked with: the results, paging and export follow it, not the inputs, which can be changed
	// freely without touching what's on screen
	const [submitted, setSubmitted] = useState<SubmittedSearch | null>(null);

	const { authFetch } = useAuth({ scopes: [] });
	const queryClient = useQueryClient();

	const ui = requestHistory ? LOOKUP_UI[requestHistory] : undefined;
	const combined = isCombinedArea(requestHistory, area);
	const pageSizes = combined ? PAGE_SIZES_COMBINED : PAGE_SIZES_SINGLE;
	const maxCodes = combined ? MAX_CODES_COMBINED : MAX_CODES_SINGLE;
	const codes = parseCodes(codesText);
	const tooManyCodes = codes.length > maxCodes;
	const canSearch = !!requestHistory && !!area && codes.length > 0 && !tooManyCodes;

	// each search and page is its own key, so the previous results are cleared while it loads
	const { data, isError, error, isFetching } = useQuery<LookupResponse>({
		queryKey: ['lookup', submitted, page],
		queryFn: ({ signal }) =>
			fetchLookup({ ...submitted!.search, page, pageSize: submitted!.pageSize, authFetch, signal }),
		retry: false,
		enabled: !!submitted,
		// don't render a previous search's cached results when navigating back to this tab
		gcTime: 0,
	});

	useEffect(() => {
		// clean up SSO redirect
		window.location.hash = '';
	}, []);

	// page sizes differ between combined and single areas
	useEffect(() => {
		setPageSize(pageSizes[0]);
	}, [combined]);

	useEffect(() => {
		if (!isError) {
			setErrorMessage(null);
			return;
		}
		setErrorMessage(
			error.message.includes('Unauthorized') ? 'Authentication failed, please log out and back in' : error.message
		);
	}, [isError, error]);

	// changing Used In picks its default area (runs after the filters reset the area)
	useEffect(() => {
		setArea(ui?.defaultArea ?? '');
	}, [requestHistory]);

	const domainLabel = USED_IN_OPTIONS.find(({ value }) => value === submitted?.search.domain)?.label;

	return (
		<>
			<Nav />
			<main>
				<div className="search-filters-top">
					<UsedInAreaFilters
						requestHistory={requestHistory}
						onRequestHistoryChange={setRequestHistory}
						translationType={area}
						onTranslationTypeChange={setArea}
						extraAreaOptions={LOOKUP_EXTRA_AREAS}
					/>
					{ui?.branchFilters && (
						<label className="search-filter-label">
							Branch
							<input
								className="lookup-filter-input"
								type="text"
								value={branch}
								placeholder="e.g. SDDE, blank = all"
								title="e.g. SDDE, blank = all"
								onChange={(e) => setBranch(e.target.value)}
							/>
						</label>
					)}
					{ui?.branchFilters && (
						<label className="lookup-exclude-en-gb">
							<input
								type="checkbox"
								checked={excludeEnGbBranches}
								onChange={(e) => setExcludeEnGbBranches(e.target.checked)}
							/>
							Exclude en-GB branches
						</label>
					)}
					{ui?.localeFilter && (
						<label className="search-filter-label">
							Locale
							<input
								className="lookup-filter-input"
								type="text"
								value={locale}
								placeholder="blank = all locales"
								title="blank = all locales"
								onChange={(e) => setLocale(e.target.value)}
							/>
						</label>
					)}
				</div>

				<form
					className="search-toolbar"
					onSubmit={(e) => {
						e.preventDefault();
						if (!canSearch) return;
						setExportError(null);
						setPage(1);
						setSubmitted({
							search: {
								domain: requestHistory as RequestHistoryDomain,
								area,
								codes,
								branch: ui?.branchFilters ? branch.trim() : '',
								excludeEnGbBranches: !!ui?.branchFilters && excludeEnGbBranches,
								locale: ui?.localeFilter ? locale.trim() : '',
								combined,
							},
							pageSize,
							// keys are compared by value, so searching again with the same inputs still refetches
							searchedAt: Date.now(),
						});
					}}
				>
					<textarea
						value={codesText}
						placeholder={(ui ?? LOOKUP_UI.ProductDomain).codesPlaceholder}
						onChange={(e) => setCodesText(e.target.value)}
					></textarea>
					<span className="lookup-code-count">
						{codes.length} / {maxCodes} codes
					</span>
					<select
						value={String(pageSize)}
						onChange={(e) => setPageSize(Number(e.target.value))}
					>
						{pageSizes.map((size) => (
							<option key={size} value={size}>
								{size} per page
							</option>
						))}
					</select>
					<div className="lookup-search-actions">
						<button type="submit" disabled={isFetching || !canSearch}>
							{isFetching ? 'Fetching...' : 'Search'}
						</button>
						{isFetching && (
							<button type="button" onClick={() => queryClient.cancelQueries({ queryKey: ['lookup'] })}>
								Cancel
							</button>
						)}
					</div>
				</form>
				{tooManyCodes && (
					<p className="error">
						At most {maxCodes} codes can be looked up for this Area ({codes.length} entered)
					</p>
				)}
				{errorMessage && <p className="error">{errorMessage}</p>}

				{data && !isFetching && !isError && (
					<>
						<p className="lookup-banner">
							Please note: Saving the changes will affect the locale translation value for every product / category, that uses the same en-GB text.
						</p>
						{data.notFound.length > 0 && <p className="no-results">Not found: {data.notFound.join(', ')}</p>}
						{data.total > 0 ? (
							<p>
								Showing page {data.page} of {data.totalPages} ({data.total} total results)
							</p>
						) : (
							<p className="no-results">No matches found for these codes.</p>
						)}
					</>
				)}

				{!isError &&
					submitted &&
					data?.items.map((item) => (
						<LookupCard
							key={`${item.code}_${item.branchNumber ?? ''}`}
							domain={submitted.search.domain}
							domainLabel={domainLabel ?? ''}
							item={item}
							collapsible={submitted.search.combined}
						/>
					))}

				{!isError && data && data.items.length > 0 && (
					<>
						<button
							className="page-action"
							disabled={isExporting || isFetching}
							onClick={async () => {
								if (!submitted) return;
								setIsExporting(true);
								setExportError(null);
								try {
									downloadCSV(await fetchAllPages(submitted.search, authFetch));
								} catch (exportFailure) {
									setExportError(
										`Export failed: ${exportFailure instanceof Error ? exportFailure.message : 'unknown error'}`
									);
								} finally {
									setIsExporting(false);
								}
							}}
						>
							{isExporting ? 'Exporting...' : 'Export CSV (all pages)'}
						</button>
						{exportError && <p className="error">{exportError}</p>}
					</>
				)}
				{!isError && data && data.items.length > 0 && (
					<div className="pagination-actions">
						<button onClick={() => page > 1 && setPage(page - 1)} disabled={isFetching || page <= 1}>
							Previous page
						</button>
						<button
							onClick={() => page < data.totalPages && setPage(page + 1)}
							disabled={isFetching || page >= data.totalPages}
						>
							Next page
						</button>
					</div>
				)}
			</main>
		</>
	);
};

const LookupCard = ({
	domain,
	domainLabel,
	item,
	collapsible,
}: {
	domain: RequestHistoryDomain;
	domainLabel: string;
	item: LookupItem;
	collapsible: boolean;
}) => {
	const ui = LOOKUP_UI[domain];
	const header = [
		['Code', item.code],
		[ui.titleLabel, item.title],
		['Branch', item.branchShortCode ? `${item.branchShortCode} (${item.branchNumber})` : undefined],
		// the locales the category's branch sells in
		['Locale', ui.branchFilters ? (item.allowedLocales.length ? item.allowedLocales : ['en-GB']).join(', ') : undefined],
	].filter((field): field is [string, string] => !!field[1]);

	return (
		<section>
			<h2 className="used-in">
				<span className="used-in-row">
					<span className="used-in-label">Used in:</span> <span className="used-in-value">{domainLabel}</span>
					{header.map(([label, value]) => (
						<span className="used-in-field" key={label}>
							<span className="used-in-label">{label}:</span> <span className="used-in-value">{value}</span>
						</span>
					))}
				</span>
			</h2>
			{item.fields.length === 0 && <p className="no-results">No translatable text for this Area.</p>}
			{item.fields.map((field) =>
				collapsible ? (
					<details className="lookup-field" key={`${field.area}_${field.itemKey ?? ''}`}>
						<summary className="lookup-field-label">
							{field.label}: {field.enGB}
						</summary>
						<LookupFieldLines domain={domain} item={item} field={field} />
					</details>
				) : (
					<div className="lookup-field" key={`${field.area}_${field.itemKey ?? ''}`}>
						<div className="lookup-field-label">{field.label}</div>
						<LookupFieldLines domain={domain} item={item} field={field} />
					</div>
				)
			)}
			{item.fields.length > 0 && item.fields.every((field) => field.locales.length === 0) && (
				<p className="no-results">
					No other locales allowed for the {ui.noun}.
				</p>
			)}
		</section>
	);
};

const LookupFieldLines = ({
	domain,
	item,
	field,
}: {
	domain: RequestHistoryDomain;
	item: LookupItem;
	field: LookupField;
}) => {
	const lines = field.locales.map((value) => (
		<LookupTranslationLine
			key={`${field.translationId}_${value.locale}`}
			domain={domain}
			code={item.code}
			branchNumber={item.branchNumber}
			area={field.area}
			itemKey={field.itemKey}
			value={value}
			hasRecord={field.hasRecord}
			autoTranslates={field.autoTranslates}
		/>
	));

	return (
		<>
			<div className="translation-line lookup-readonly-line">
				<input className="locale-label" type="text" value="en-GB" disabled />
				<textarea className="translation-text" value={field.enGB} disabled />
				{/* keeps the box the same width as the editable rows, which have a Save button */}
				<button type="button" className="lookup-save-spacer" tabIndex={-1} aria-hidden="true">
					Save
				</button>
			</div>
			{field.locales.length === 1 ? (
				lines
			) : (
				field.locales.length > 1 && (
					<details className="lookup-other-locales">
						<summary>Other locales ({field.locales.length})</summary>
						{lines}
					</details>
				)
			)}
		</>
	);
};

type LookupSearch = {
	domain: RequestHistoryDomain;
	area: string;
	codes: string[];
	branch: string;
	excludeEnGbBranches: boolean;
	locale: string;
	combined: boolean;
};

type SubmittedSearch = {
	search: LookupSearch;
	pageSize: number;
	searchedAt: number;
};

// every page of a search, fetched one after another at the largest page size the area allows
async function fetchAllPages(search: LookupSearch, authFetch: AuthFetch): Promise<LookupItem[]> {
	const pageSizes = search.combined ? PAGE_SIZES_COMBINED : PAGE_SIZES_SINGLE;
	const pageSize = pageSizes[pageSizes.length - 1];
	const items: LookupItem[] = [];
	let totalPages = 1;
	for (let page = 1; page <= totalPages; page++) {
		const response = await fetchLookup({ ...search, page, pageSize, authFetch });
		items.push(...response.items);
		totalPages = response.totalPages;
	}
	return items;
}

async function fetchLookup({
	domain,
	area,
	codes,
	branch,
	excludeEnGbBranches,
	locale,
	page,
	pageSize,
	authFetch,
	signal,
}: {
	domain: RequestHistoryDomain;
	area: string;
	codes: string[];
	branch: string;
	excludeEnGbBranches: boolean;
	locale: string;
	page: number;
	pageSize: number;
	authFetch: AuthFetch;
	signal?: AbortSignal;
}): Promise<LookupResponse> {
	// posted, as up to 1000 codes can be too long for a url
	const response = await authFetch('/api/overrides/lookup/search', {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify({
			domain,
			area,
			codes,
			page,
			pageSize,
			...(branch && { branch }),
			...(locale && { locale }),
			...(excludeEnGbBranches && { excludeEnGbBranches }),
		}),
		signal,
	});

	if (!response.ok) {
		const responseBody: unknown = await response.json().catch(() => response.statusText);
		throw new Error(typeof responseBody === 'string' ? responseBody : response.statusText);
	}
	return (await response.json()) as LookupResponse;
}

function escapeCSV(value: string | number | undefined): string {
	return `"${String(value ?? '').replace(/"/g, '""')}"`;
}

// one row per field on the current page, with each locale's text on the product / category
function downloadCSV(items: LookupItem[]) {
	const locales = [
		...new Set(items.flatMap(({ fields }) => fields.flatMap((field) => field.locales.map(({ locale }) => locale)))),
	].sort();
	const header = ['code', 'branch', 'area', 'itemKey', 'label', 'translationId', 'en-GB', ...locales];

	const rows = items.flatMap((item) =>
		item.fields.map((field) => {
			const valueByLocale = new Map(
				field.locales.map(({ locale, liveText }) => [locale, liveText])
			);
			return [
				item.code,
				item.branchShortCode,
				field.area,
				field.itemKey,
				field.label,
				field.translationId,
				field.enGB,
				...locales.map((locale) => valueByLocale.get(locale)),
			];
		})
	);

	const csvContent = [header, ...rows].map((row) => row.map(escapeCSV).join(',')).join('\n') + '\n';
	// BOM so Excel reads accented characters as UTF-8
	const blob = new Blob(['\uFEFF', csvContent], { type: 'text/csv;charset=utf-8;' });
	const url = URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.setAttribute('href', url);
	link.setAttribute('download', 'lookup.csv');
	link.click();
	URL.revokeObjectURL(url);
}
